import React, { useEffect, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  ProfitLossDto,
  ProfitLossFieldKey,
  ProfitLossFormValues,
  ProfitLossInputDto,
  ProfitLossUploadResult,
} from "interfaces/ProfitLoss.interface";
import {
  downloadProfitLossTemplate,
  getProfitLoss,
  getStoredStoreNumber,
  uploadProfitLossWorkbook,
  upsertProfitLoss,
} from "scripts/profitLossApi";

interface ProfitLossTileModalProps {
  open: boolean;
  onClose: () => void;
  employeePin?: string;
}

interface SectionFieldConfig {
  key: ProfitLossFieldKey;
  label: string;
}

interface SectionConfig {
  key: keyof ProfitLossInputDto;
  title: string;
  totalLabel: string;
  fields: SectionFieldConfig[];
}

const getCurrentQuarter = (): string => `Q${Math.floor(new Date().getMonth() / 3) + 1}`;

const createEmptyFormValues = (): ProfitLossFormValues => ({
  grossSales: "",
  fruitAndProduce: "",
  arrangementProductionAndDelivery: "",
  fudgeAndCandies: "",
  otherCogs: "",
  storeStaff: "",
  management: "",
  payrollTaxes: "",
  payrollBenefits: "",
  payrollOther: "",
  rent: "",
  propertyTaxesCams: "",
  utilities: "",
  propertyOther: "",
  fuel: "",
  repairs: "",
  autoInsurance: "",
  autoRental: "",
  thirdPartyDeliveryFee: "",
  automobileOther: "",
});

const SECTIONS: SectionConfig[] = [
  {
    key: "costOfGoodsSold",
    title: "Cost of Goods Sold",
    totalLabel: "Total Cost of Goods Sold",
    fields: [
      { key: "fruitAndProduce", label: "Fruit and Produce" },
      { key: "arrangementProductionAndDelivery", label: "Arrangement Production & Delivery" },
      { key: "fudgeAndCandies", label: "Fudge & Candies" },
      { key: "otherCogs", label: "Other COGS" },
    ],
  },
  {
    key: "payrollExpenses",
    title: "Payroll Expenses",
    totalLabel: "Total Payroll Expenses",
    fields: [
      { key: "storeStaff", label: "Store Staff" },
      { key: "management", label: "Management" },
      { key: "payrollTaxes", label: "Payroll Taxes" },
      { key: "payrollBenefits", label: "Payroll Benefits" },
      { key: "payrollOther", label: "Payroll - Other" },
    ],
  },
  {
    key: "propertyExpense",
    title: "Property Expense",
    totalLabel: "Total Property Expense",
    fields: [
      { key: "rent", label: "Rent" },
      { key: "propertyTaxesCams", label: "Property Taxes/CAMs" },
      { key: "utilities", label: "Utilities" },
      { key: "propertyOther", label: "Property - Other" },
    ],
  },
  {
    key: "automobileExpenses",
    title: "Automobile Expenses",
    totalLabel: "Total Automobile Expenses",
    fields: [
      { key: "fuel", label: "Fuel" },
      { key: "repairs", label: "Repairs" },
      { key: "autoInsurance", label: "Auto Insurance" },
      { key: "autoRental", label: "Auto Rental" },
      { key: "thirdPartyDeliveryFee", label: "Third Party Delivery Fee" },
      { key: "automobileOther", label: "Automobile - Other" },
    ],
  },
];

const toInputValue = (value: number | null | undefined): string => {
  if (typeof value !== "number" || Number.isNaN(value)) return "";
  return String(value);
};

const parseAmount = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const sumFields = (formValues: ProfitLossFormValues, keys: ProfitLossFieldKey[]): number | null => {
  const values = keys
    .map((key) => parseAmount(formValues[key]))
    .filter((value): value is number => typeof value === "number");

  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0);
};

const formatCurrency = (value: number | null): string => {
  if (value === null) return "--";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDisplayValue = (value: string): string => {
  return `${value.trim() ? formatCurrency(parseAmount(value)) : "--"}`;
};

const buildFormValuesFromRecord = (record: ProfitLossDto): ProfitLossFormValues => ({
  grossSales: toInputValue(record.metrics.grossSales),
  fruitAndProduce: toInputValue(record.metrics.costOfGoodsSold.fruitAndProduce),
  arrangementProductionAndDelivery: toInputValue(record.metrics.costOfGoodsSold.arrangementProductionAndDelivery),
  fudgeAndCandies: toInputValue(record.metrics.costOfGoodsSold.fudgeAndCandies),
  otherCogs: toInputValue(record.metrics.costOfGoodsSold.otherCogs),
  storeStaff: toInputValue(record.metrics.payrollExpenses.storeStaff),
  management: toInputValue(record.metrics.payrollExpenses.management),
  payrollTaxes: toInputValue(record.metrics.payrollExpenses.payrollTaxes),
  payrollBenefits: toInputValue(record.metrics.payrollExpenses.payrollBenefits),
  payrollOther: toInputValue(record.metrics.payrollExpenses.payrollOther),
  rent: toInputValue(record.metrics.propertyExpense.rent),
  propertyTaxesCams: toInputValue(record.metrics.propertyExpense.propertyTaxesCams),
  utilities: toInputValue(record.metrics.propertyExpense.utilities),
  propertyOther: toInputValue(record.metrics.propertyExpense.propertyOther),
  fuel: toInputValue(record.metrics.automobileExpenses.fuel),
  repairs: toInputValue(record.metrics.automobileExpenses.repairs),
  autoInsurance: toInputValue(record.metrics.automobileExpenses.autoInsurance),
  autoRental: toInputValue(record.metrics.automobileExpenses.autoRental),
  thirdPartyDeliveryFee: toInputValue(record.metrics.automobileExpenses.thirdPartyDeliveryFee),
  automobileOther: toInputValue(record.metrics.automobileExpenses.automobileOther),
});

const buildPayload = (formValues: ProfitLossFormValues): ProfitLossInputDto => ({
  grossSales: parseAmount(formValues.grossSales),
  costOfGoodsSold: {
    fruitAndProduce: parseAmount(formValues.fruitAndProduce),
    arrangementProductionAndDelivery: parseAmount(formValues.arrangementProductionAndDelivery),
    fudgeAndCandies: parseAmount(formValues.fudgeAndCandies),
    otherCogs: parseAmount(formValues.otherCogs),
    totalCostOfGoodsSold: sumFields(formValues, [
      "fruitAndProduce",
      "arrangementProductionAndDelivery",
      "fudgeAndCandies",
      "otherCogs",
    ]),
  },
  payrollExpenses: {
    storeStaff: parseAmount(formValues.storeStaff),
    management: parseAmount(formValues.management),
    payrollTaxes: parseAmount(formValues.payrollTaxes),
    payrollBenefits: parseAmount(formValues.payrollBenefits),
    payrollOther: parseAmount(formValues.payrollOther),
    totalPayrollExpenses: sumFields(formValues, [
      "storeStaff",
      "management",
      "payrollTaxes",
      "payrollBenefits",
      "payrollOther",
    ]),
  },
  propertyExpense: {
    rent: parseAmount(formValues.rent),
    propertyTaxesCams: parseAmount(formValues.propertyTaxesCams),
    utilities: parseAmount(formValues.utilities),
    propertyOther: parseAmount(formValues.propertyOther),
    totalPropertyExpense: sumFields(formValues, [
      "rent",
      "propertyTaxesCams",
      "utilities",
      "propertyOther",
    ]),
  },
  automobileExpenses: {
    fuel: parseAmount(formValues.fuel),
    repairs: parseAmount(formValues.repairs),
    autoInsurance: parseAmount(formValues.autoInsurance),
    autoRental: parseAmount(formValues.autoRental),
    thirdPartyDeliveryFee: parseAmount(formValues.thirdPartyDeliveryFee),
    automobileOther: parseAmount(formValues.automobileOther),
    totalAutomobileExpenses: sumFields(formValues, [
      "fuel",
      "repairs",
      "autoInsurance",
      "autoRental",
      "thirdPartyDeliveryFee",
      "automobileOther",
    ]),
  },
});

const ProfitLossTileModal: React.FC<ProfitLossTileModalProps> = ({ open, onClose, employeePin = "" }) => {
  const [fiscalYear, setFiscalYear] = useState<number>(new Date().getFullYear());
  const [quarter, setQuarter] = useState<string>(getCurrentQuarter());
  const [formValues, setFormValues] = useState<ProfitLossFormValues>(createEmptyFormValues);
  const [loadedFormValues, setLoadedFormValues] = useState<ProfitLossFormValues>(createEmptyFormValues);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [hasExistingRecord, setHasExistingRecord] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editConfirmOpen, setEditConfirmOpen] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<ProfitLossUploadResult | null>(null);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const storeNumber = getStoredStoreNumber();

  useEffect(() => {
    if (!open) return;

    setIsEditMode(false);
    setEditConfirmOpen(false);

    if (!storeNumber) {
      setError("Unable to determine the logged-in store number.");
      setFormValues(createEmptyFormValues());
      setLoadedFormValues(createEmptyFormValues());
      setLastUpdated("");
      setHasExistingRecord(false);
      return;
    }

    let isMounted = true;

    const loadProfitLoss = async () => {
      setLoading(true);
      setError("");
      setSuccess("");

      try {
        const record = await getProfitLoss({
          storeNumber,
          quarter,
          fiscalYear,
        });

        if (!isMounted) return;

        if (record) {
          const nextValues = buildFormValuesFromRecord(record);
          setFormValues(nextValues);
          setLoadedFormValues(nextValues);
          setLastUpdated(record.updatedOn);
          setHasExistingRecord(true);
        } else {
          const emptyValues = createEmptyFormValues();
          setFormValues(emptyValues);
          setLoadedFormValues(emptyValues);
          setLastUpdated("");
          setHasExistingRecord(false);
        }
      } catch (loadError) {
        if (!isMounted) return;
        setError(loadError instanceof Error ? loadError.message : "Unable to load Profit & Loss data.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    void loadProfitLoss();

    return () => {
      isMounted = false;
    };
  }, [open, storeNumber, quarter, fiscalYear, refreshIndex]);

  const handleClose = () => {
    setError("");
    setSuccess("");
    setIsEditMode(false);
    setEditConfirmOpen(false);
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setFormValues(loadedFormValues);
    onClose();
  };

  const handleRequestEdit = () => {
    if (hasExistingRecord) {
      setEditConfirmOpen(true);
      return;
    }

    setIsEditMode(true);
    setError("");
    setSuccess("");
  };

  const handleConfirmEdit = () => {
    setEditConfirmOpen(false);
    setIsEditMode(true);
    setError("");
    setSuccess("");
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setError("");
    setSuccess("");
    setFormValues(loadedFormValues);
  };

  const handleChange = (key: ProfitLossFieldKey, value: string) => {
    setFormValues((current) => ({
      ...current,
      [key]: value,
    }));
    if (success) setSuccess("");
    if (error) setError("");
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!storeNumber) {
      setError("Unable to determine the logged-in store number.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const payload = buildPayload(formValues);
      const record = await upsertProfitLoss(
        {
          storeNumber,
          quarter,
          fiscalYear,
          ...(employeePin ? { employeePin } : {}),
        },
        payload
      );

      const nextValues = buildFormValuesFromRecord(record);
      setFormValues(nextValues);
      setLoadedFormValues(nextValues);
      setLastUpdated(record.updatedOn);
      setHasExistingRecord(true);
      setIsEditMode(false);
      setSuccess("Profit & Loss data saved successfully.");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to save Profit & Loss data.");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    setError("");
    setSuccess("");

    try {
      await downloadProfitLossTemplate();
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : "Unable to download the template.");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input so selecting the same file again still fires onChange.
    event.target.value = "";

    if (!file) return;

    if (!employeePin) {
      setError("Your employee PIN is required to upload. Re-open Profit & Loss and enter your PIN.");
      return;
    }

    setUploading(true);
    setError("");
    setSuccess("");
    setUploadResult(null);

    try {
      const result = await uploadProfitLossWorkbook(file, employeePin);
      setUploadResult(result);

      if (result.saved > 0) {
        setSuccess(`Uploaded ${result.saved} of ${result.totalRecords} record(s).`);
        // Refresh the currently displayed quarter so saved values are reflected.
        setRefreshIndex((current) => current + 1);
      } else {
        setError("No records were saved. Review the details below and try again.");
      }
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "Unable to upload the file.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          fontFamily: "Poppins, sans-serif",
          maxHeight: "92vh",
          display: "flex",
        },
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: "100%",
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#E10600",
            color: "#FFFFFF",
            fontFamily: "Poppins, sans-serif",
            fontSize: 20,
            fontWeight: 600,
            px: 3,
            py: 2,
            flexShrink: 0,
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          Profit &amp; Loss
          <Typography component="div" sx={{ fontSize: 13, opacity: 0.9, mt: 0.5, fontFamily: "Poppins, sans-serif" }}>
            Submit quarterly store expenses for the logged-in location.
          </Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            px: 3,
            py: 3,
            backgroundColor: "#F7F4EF",
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Store Number"
                value={storeNumber}
                fullWidth
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Fiscal Year"
                type="number"
                value={fiscalYear}
                onChange={(event) => setFiscalYear(Number(event.target.value) || new Date().getFullYear())}
                fullWidth
                inputProps={{ min: 2000, step: 1 }}
                disabled={isEditMode}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                select
                label="Quarter"
                value={quarter}
                onChange={(event) => setQuarter(event.target.value)}
                fullWidth
                disabled={isEditMode}
              >
                {["Q1", "Q2", "Q3", "Q4"].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, minHeight: 28, gap: 2 }}>
            <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: 13, color: "#5D5D5D" }}>
              {isEditMode
                ? "Editing is enabled for the selected fiscal period."
                : hasExistingRecord
                  ? "Values are shown in read-only mode until edit is confirmed."
                  : "No P&L has been submitted for this fiscal period yet."}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
              {!isEditMode && (
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleRequestEdit}
                  disabled={loading}
                  sx={{
                    borderColor: "#E10600",
                    color: "#E10600",
                    fontFamily: "Poppins, sans-serif",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      borderColor: "#B90500",
                      backgroundColor: "rgba(225, 6, 0, 0.04)",
                    },
                  }}
                >
                  {hasExistingRecord ? "Edit P&L" : "Submit new P&L"}
                </Button>
              )}
              {loading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} sx={{ color: "#E10600" }} />
                  <Typography sx={{ fontSize: 13, color: "#5D5D5D", fontFamily: "Poppins, sans-serif", whiteSpace: "nowrap" }}>
                    Loading saved values
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {lastUpdated && !loading && lastUpdated < Date.now().toLocaleString() && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Loaded saved values for this quarter. Last updated {new Date(lastUpdated).toLocaleString()}.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Paper
            elevation={0}
            sx={{
              mb: 2,
              borderRadius: 3,
              border: "1px solid rgba(225, 6, 0, 0.12)",
              overflow: "hidden",
              boxShadow: "0px 7px 15px rgba(0, 0, 0, 0.08)",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                backgroundColor: "#FFF2F1",
                borderBottom: "1px solid rgba(225, 6, 0, 0.12)",
              }}
            >
              <Typography sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, color: "#2E2E2E" }}>
                Import / Export (Excel)
              </Typography>
            </Box>
            <Box sx={{ p: 2, backgroundColor: "#FFFFFF" }}>
              <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: 13, color: "#5D5D5D", mb: 1 }}>
                Download the template, fill one column per store/quarter, then upload it. Each value
                column is saved as a separate Profit &amp; Loss record.
              </Typography>
              <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: 12, fontStyle: "italic", color: "#E10600", mb: 1.5 }}>
                Only fields matching the template are saved — extra columns won&apos;t be used.
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleDownloadTemplate}
                  disabled={downloadingTemplate}
                  sx={{
                    borderColor: "#E10600",
                    color: "#E10600",
                    fontFamily: "Poppins, sans-serif",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": { borderColor: "#B90500", backgroundColor: "rgba(225, 6, 0, 0.04)" },
                  }}
                >
                  {downloadingTemplate ? "Preparing..." : "Download Template"}
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || !storeNumber}
                  sx={{
                    backgroundColor: "#E10600",
                    color: "#FFFFFF",
                    fontFamily: "Poppins, sans-serif",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": { backgroundColor: "#B90500" },
                  }}
                >
                  {uploading ? "Uploading..." : "Upload Filled Template"}
                </Button>
                {uploading && <CircularProgress size={18} sx={{ color: "#E10600" }} />}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  style={{ display: "none" }}
                  onChange={handleUploadFile}
                />
              </Box>
              {uploadResult && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity={uploadResult.failed === 0 ? "success" : "warning"} sx={{ mb: 1 }}>
                    Saved {uploadResult.saved} of {uploadResult.totalRecords} record(s).
                    {uploadResult.failed > 0 ? ` ${uploadResult.failed} failed.` : ""}
                  </Alert>
                  {uploadResult.results
                    .filter((row) => row.status === "failed")
                    .map((row) => (
                      <Typography
                        key={row.column}
                        sx={{ fontFamily: "Poppins, sans-serif", fontSize: 12, color: "#B90500" }}
                      >
                        Column {row.column}: {(row.errors && row.errors.join(", ")) || "Failed"}
                      </Typography>
                    ))}
                </Box>
              )}
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mb: 2,
              borderRadius: 3,
              border: "1px solid rgba(225, 6, 0, 0.12)",
              overflow: "hidden",
              boxShadow: "0px 7px 15px rgba(0, 0, 0, 0.08)",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                backgroundColor: "#FFF2F1",
                borderBottom: "1px solid rgba(225, 6, 0, 0.12)",
              }}
            >
              <Typography sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, color: "#2E2E2E" }}>
                Gross Sales
              </Typography>
            </Box>
            <Box sx={{ p: 2, backgroundColor: "#FFFFFF" }}>
              {isEditMode ? (
                <TextField
                  label="Gross Sales"
                  type="number"
                  value={formValues.grossSales}
                  onChange={(event) => handleChange("grossSales", event.target.value)}
                  fullWidth
                  inputProps={{ step: "0.01" }}
                  InputLabelProps={{ shrink: true }}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    border: "1px solid #E3E3E3",
                    borderRadius: 2,
                    px: 1.75,
                    py: 1.5,
                    backgroundColor: "#FAFAFA",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#2E2E2E",
                      lineHeight: 1.4,
                    }}
                  >
                    Gross Sales
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#2E2E2E",
                      whiteSpace: "nowrap",
                    }}
                  >
                    ${formatDisplayValue(formValues.grossSales)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          <Grid container spacing={2}>
            {SECTIONS.map((section) => {
              const total = sumFields(
                formValues,
                section.fields.map((field) => field.key)
              );

              return (
                <Grid key={section.key} size={{ xs: 12, md: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid rgba(225, 6, 0, 0.12)",
                      overflow: "hidden",
                      boxShadow: "0px 7px 15px rgba(0, 0, 0, 0.08)",
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        backgroundColor: "#FFF2F1",
                        borderBottom: "1px solid rgba(225, 6, 0, 0.12)",
                      }}
                    >
                      <Typography sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, color: "#2E2E2E" }}>
                        {section.title}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2, backgroundColor: "#FFFFFF" }}>
                      <Grid container spacing={1.5}>
                        {section.fields.map((field) => (
                          <Grid key={field.key} size={12}>
                            {isEditMode ? (
                              <TextField
                                label={field.label}
                                type="number"
                                value={formValues[field.key]}
                                onChange={(event) => handleChange(field.key, event.target.value)}
                                fullWidth
                                inputProps={{ step: "0.01" }}
                                InputLabelProps={{ shrink: true }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 2,
                                  border: "1px solid #E3E3E3",
                                  borderRadius: 2,
                                  px: 1.75,
                                  py: 1.5,
                                  backgroundColor: "#FAFAFA",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: "Poppins, sans-serif",
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: "#2E2E2E",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {field.label}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontFamily: "Poppins, sans-serif",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: "#2E2E2E",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  ${formatDisplayValue(formValues[field.key])}
                                </Typography>
                              </Box>
                            )}
                          </Grid>
                        ))}
                      </Grid>
                      <Divider sx={{ my: 2 }} />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 1,
                        }}
                      >
                        <Typography sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, color: "#2E2E2E" }}>
                          {section.totalLabel}
                        </Typography>
                        <Typography sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 700, color: "#E10600" }}>
                          ${formatCurrency(total)}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            backgroundColor: "#FFFFFF",
            flexShrink: 0,
            borderTop: "1px solid rgba(0, 0, 0, 0.08)",
          }}
        >
          <Button
            type="button"
            onClick={handleClose}
            sx={{
              color: "#6D6D6D",
              fontFamily: "Poppins, sans-serif",
              textTransform: "none",
            }}
          >
            Close
          </Button>
          {isEditMode && (
            <Button
              type="button"
              onClick={handleCancelEdit}
              sx={{
                color: "#6D6D6D",
                fontFamily: "Poppins, sans-serif",
                textTransform: "none",
              }}
            >
              Cancel Edit
            </Button>
          )}
          {isEditMode && (
            <Button
              type="submit"
              variant="contained"
              disabled={saving || loading || !storeNumber}
              sx={{
                backgroundColor: "#E10600",
                color: "#FFFFFF",
                fontFamily: "Poppins, sans-serif",
                textTransform: "none",
                px: 2.5,
                "&:hover": {
                  backgroundColor: "#B90500",
                },
              }}
            >
              {saving ? "Saving..." : "Save Profit & Loss"}
            </Button>
          )}
        </DialogActions>
      </Box>
      <Dialog
        open={editConfirmOpen}
        onClose={() => setEditConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            fontFamily: "Poppins, sans-serif",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: 18,
            fontWeight: 600,
          }}
        >
          Confirm Edit
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: 14, color: "#2E2E2E" }}>
            This will edit the already submitted P &amp; L. Do you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            type="button"
            onClick={() => setEditConfirmOpen(false)}
            sx={{
              color: "#6D6D6D",
              fontFamily: "Poppins, sans-serif",
              textTransform: "none",
            }}
          >
            No
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleConfirmEdit}
            sx={{
              backgroundColor: "#E10600",
              color: "#FFFFFF",
              fontFamily: "Poppins, sans-serif",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#B90500",
              },
            }}
          >
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ProfitLossTileModal;
